/**
 * Where "has this tour been completed" is remembered.
 *
 * A seam, not a hard dependency on localStorage — swapping in a server-backed
 * store for production is a constructor argument, not a rewrite.
 */

export interface TourProgressStore {
  read(): Record<string, boolean>;
  write(progress: Record<string, boolean>): void;
}

/**
 * The sandbox store. The `sandbox_` prefix is deliberate: it makes every key
 * this build writes identifiable at a glance, so clearing demo state never has
 * to guess which keys belong to it.
 */
export const SANDBOX_PROGRESS_KEY = 'sandbox_tour_progress_v1';

export function createLocalProgressStore(key = SANDBOX_PROGRESS_KEY): TourProgressStore {
  return {
    read() {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch {
        // Private-mode browsing throws on storage access. A tour that crashes
        // the shell is far worse than one that forgets it was completed.
        return {};
      }
    },
    write(progress) {
      try {
        window.localStorage.setItem(key, JSON.stringify(progress));
      } catch {
        /* ignore */
      }
    },
  };
}

/**
 * The default for a NON-sandbox build: in-memory only.
 *
 * 🔴 Deliberately NOT localStorage. In production, "has this user seen the
 * tour" is ACCOUNT state, not browser state — a shared machine would otherwise
 * suppress the tour for the next person, and the same user on a second device
 * would be shown it again. Production should pass `createApiProgressStore`;
 * until it does, forgetting on reload is the honest behaviour.
 */
export function createMemoryProgressStore(): TourProgressStore {
  let state: Record<string, boolean> = {};
  return {
    read: () => state,
    write: (progress) => {
      state = progress;
    },
  };
}

/** Server-backed store for production. Writes are fire-and-forget. */
export function createApiProgressStore(io: {
  get(): Record<string, boolean>;
  put(progress: Record<string, boolean>): void;
}): TourProgressStore {
  return { read: io.get, write: io.put };
}
